import React from 'react';
import { motion } from 'framer-motion';

const TechStack = () => {
  const technologies = [
    { name: 'AWS', category: 'Cloud' },
    { name: 'Google Cloud', category: 'Cloud' },
    { name: 'Azure', category: 'Cloud' },
    { name: 'Terraform', category: 'IaC' },
    { name: 'Kubernetes', category: 'Container' },
    { name: 'Python', category: 'Language' },
    { name: 'React', category: 'Frontend' },
    { name: 'Node.js', category: 'Backend' },
    { name: 'OpenAI', category: 'AI' },
    { name: 'Anthropic', category: 'AI' },
    { name: 'Docker', category: 'Container' },
    { name: 'PostgreSQL', category: 'Database' },
  ];

  // Duplicate for seamless loop
  const duplicatedTech = [...technologies, ...technologies];

  return (
    <section className="py-24 px-6 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center px-4 py-2 bg-cream rounded-full mb-6 border border-gray-100"
          >
            <span className="text-sm font-medium text-muted-grey">
              Technology Stack
            </span>
          </motion.div>

          <h2 className="text-5xl md:text-6xl font-bold text-dark-slate mb-6 tracking-tight">
            Modern Tools,
            <br />
            <span className="text-muted-blue-grey">Proven Expertise</span>
          </h2>

          <p className="text-xl text-muted-grey max-w-3xl mx-auto leading-relaxed">
            Deep experience across the modern technology stack, from cloud infrastructure
            to AI integration and everything in between.
          </p>
        </motion.div>
      </div>

      {/* Marquee */}
      <div className="relative">
        {/* Gradient Overlays */}
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10"></div>
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10"></div>

        {/* Scrolling Container */}
        <motion.div
          className="flex gap-6"
          animate={{
            x: [0, -1920], // Adjust based on content width
          }}
          transition={{
            x: {
              repeat: Infinity,
              repeatType: "loop",
              duration: 30,
              ease: "linear",
            },
          }}
        >
          {duplicatedTech.map((tech, index) => (
            <div
              key={index}
              className="flex-shrink-0 bg-white border border-gray-200 rounded-2xl px-8 py-6 min-w-[200px] hover:border-gray-300 hover:shadow-md transition-all"
            >
              <div className="text-center">
                <div className="text-2xl font-bold text-dark-slate mb-2">
                  {tech.name}
                </div>
                <div className="text-sm text-muted-grey">
                  {tech.category}
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Tech Categories Grid */}
      <div className="max-w-7xl mx-auto mt-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: '☁️', title: 'Multi-Cloud', count: '3 Platforms' },
            { icon: '🤖', title: 'AI/ML', count: 'LLM Integration' },
            { icon: '🔧', title: 'DevOps', count: 'Full CI/CD' },
            { icon: '📊', title: 'Data', count: 'Analytics & ETL' },
          ].map((category, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="bg-cream rounded-2xl p-6 text-center border border-gray-100 hover:border-gray-200 transition-all cursor-pointer"
            >
              <div className="text-4xl mb-3">{category.icon}</div>
              <div className="font-bold text-dark-slate mb-1">{category.title}</div>
              <div className="text-sm text-muted-grey">{category.count}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TechStack;
